import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { campaignAPI, uploadAPI, Campaign, CampaignStats } from '../services/api';
import TestSendBox from '../components/TestSendBox';
import ScheduleBox from '../components/ScheduleBox';
import ViberMessagePreview from '../components/ViberMessagePreview';
import { ViberMessageOptions } from '../types/viber.types';
import Icon from '../components/Icon';
import StatTile from '../components/ui/StatTile';
import StatusBadge from '../components/ui/StatusBadge';
import ChannelBubble from '../components/ui/ChannelBubble';
import Ring from '../components/ui/Ring';
import Empty from '../components/ui/Empty';
import Pagination from '../components/Pagination';
import { channelOf, CHANNEL_META } from '../utils/channel';
import { fmt, pct, fmtDate } from '../utils/format';
import { useT } from '../i18n/LanguageContext';
import { useAuth } from '../context/AuthContext';

interface ViberStats {
  total: number; sent: number; dispatched?: number; delivered: number; undelivered?: number; seen: number;
  failed: number; pending: number; cost?: { rsd: number; eur: number }; totalCost: number;
  recentSends: number; deliveryRate: string; failureRate: string;
}

const recipientInitials = (n?: string, fallback?: string) => {
  const base = n || fallback || '';
  const parts = base.trim().split(/\s+/);
  return (parts.slice(0, 2).map((p) => p[0] || '').join('') || base[0] || '?').toUpperCase();
};

const CampaignDetail: React.FC = () => {
  const t = useT();
  const { role } = useAuth();
  const isSuperAdmin = role === 'super_admin';
  const { id } = useParams();
  const navigate = useNavigate();

  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [stats, setStats] = useState<CampaignStats | null>(null);
  const [viberStats, setViberStats] = useState<ViberStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [viberPreview, setViberPreview] = useState<ViberMessageOptions | null>(null);
  const [recipients, setRecipients] = useState<any[]>([]);
  const [recipPage, setRecipPage] = useState(1);
  const [recipLimit, setRecipLimit] = useState(10);
  const [recipStatus, setRecipStatus] = useState('all');
  const [recipTotal, setRecipTotal] = useState(0);
  const [recipTotalPages, setRecipTotalPages] = useState(1);
  const [recipLoading, setRecipLoading] = useState(false);
  const [testOpen, setTestOpen] = useState(false);

  const loadRecipients = async () => {
    setRecipLoading(true);
    try {
      const res = await uploadAPI.getRecipients(parseInt(id!), recipPage, recipLimit, recipStatus);
      const body: any = res.data;
      const rows = body?.data?.recipients || body?.recipients || [];
      const pg = body?.data?.pagination;
      setRecipients(Array.isArray(rows) ? rows : []);
      setRecipTotal(pg?.total ?? rows.length);
      setRecipTotalPages(pg?.totalPages ?? 1);
    } catch (err) {
      console.error('Failed to load recipients:', err);
      setRecipients([]);
    } finally {
      setRecipLoading(false);
    }
  };

  useEffect(() => {
    loadRecipients();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, recipPage, recipLimit, recipStatus]);

  const downloadExport = async (status: string) => {
    if (!campaign) return;
    try {
      const res = await campaignAPI.exportRecipients(campaign.id, status);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = `${(campaign.name || 'kampanja').replace(/[^a-z0-9]/gi, '_')}_${status}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      setError(t('Greška prilikom exporta', 'Export failed'));
    }
  };

  useEffect(() => {
    loadCampaign();
    // Auto-refresh stats every 3 seconds if campaign is sending
    const interval = setInterval(() => {
      if (campaign && campaign.status === 'sending') loadCampaign();
    }, 3000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, campaign?.status]);

  const loadCampaign = async () => {
    try {
      const response = await campaignAPI.getOne(parseInt(id!));
      if (response.data.success) {
        const c = response.data.data.campaign;
        setCampaign(c);
        setStats(response.data.data.stats);
        // Viber/SMS via NTH report real delivered/seen — load them for the report.
        if (c.channelId === 3 || c.viberRichContent) {
          try {
            const vs = await campaignAPI.getViberStats(c.id);
            if (vs.data.success) setViberStats(vs.data.data as ViberStats);
          } catch {
            /* ignore */
          }
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) setUploadFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!uploadFile) {
      alert(t('Molimo izaberite Excel fajl', 'Please select an Excel file'));
      return;
    }
    setUploading(true);
    setError('');
    setSuccess('');
    try {
      const response = await uploadAPI.uploadRecipients(parseInt(id!), uploadFile);
      setSuccess(t(`Uspešno upload-ovano ${response.data.data.totalUploaded} primaoca!`, `Successfully uploaded ${response.data.data.totalUploaded} recipients!`));
      setUploadFile(null);
      loadCampaign();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleStartCampaign = async () => {
    if (!window.confirm(t('Da li ste sigurni da želite da pokrenete slanje kampanje?', 'Are you sure you want to start sending this campaign?'))) return;
    try {
      await campaignAPI.start(parseInt(id!));
      setSuccess(t('Kampanja je pokrenuta! Poruke se šalju u pozadini.', 'Campaign started! Messages are being sent in the background.'));
      loadCampaign();
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handleClone = async () => {
    try {
      const res = await campaignAPI.clone(parseInt(id!));
      if (res.data.success) {
        const c = res.data.data.campaign;
        const v = c.channelId === 3 || !!c.viberRichContent;
        navigate(v ? `/campaigns/viber/${c.id}/edit` : `/campaigns/${c.id}/edit`);
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const handlePreview = async () => {
    try {
      const viber = campaign?.channelId === 3 || !!campaign?.viberRichContent;
      if (viber) {
        const rc = campaign?.viberRichContent ? JSON.parse(campaign.viberRichContent) : {};
        setViberPreview({
          text: rc.text || '',
          label: rc.label || 'PROMOTION',
          button: rc.buttonCaption ? { caption: rc.buttonCaption, action: rc.buttonAction } : undefined,
          image: rc.image,
          video: rc.video,
          carousel: rc.carousel,
          tag: rc.tag,
          ttl: rc.ttl || 86400,
        });
        setShowPreview(true);
        return;
      }
      const response = await campaignAPI.preview(parseInt(id!));
      if (response.data.success) {
        setViberPreview(null);
        setPreviewHtml(response.data.data.htmlContent);
        setShowPreview(true);
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await uploadAPI.downloadTemplate();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'recipient_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err: any) {
      setError(t('Greška prilikom preuzimanja template-a', 'Error while downloading the template'));
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="card card-pad muted">{t('Učitavanje…', 'Loading…')}</div>
      </div>
    );
  }
  if (!campaign) {
    return (
      <div className="page">
        <div className="card card-pad" style={{ color: 'var(--danger-strong)' }}>
          <Icon name="alert" size={16} /> {t('Kampanja nije pronađena', 'Campaign not found')}
        </div>
      </div>
    );
  }

  const ch = channelOf(campaign);
  const isViber = ch === 'viber';
  const isSms = ch === 'sms';
  const total = campaign.totalRecipients || 0;
  // "Poslato" = dispatched (sent+delivered+queued) = everything handed to the
  // provider, so it matches the recipient total — not the not-yet-delivered slice.
  const dispatchedVal = isViber
    ? (viberStats?.dispatched ?? viberStats?.sent ?? campaign.sentCount)
    : (stats?.sent ?? campaign.sentCount);
  const deliveredVal = isViber ? viberStats?.delivered ?? campaign.sentCount : (stats?.delivered ?? campaign.sentCount);
  const undeliveredVal = Math.max(0, total - deliveredVal);
  const seenVal = isViber ? viberStats?.seen ?? 0 : 0;
  const failedVal = isViber ? viberStats?.failed ?? campaign.failedCount : campaign.failedCount;
  const costObj = isViber ? viberStats?.cost : stats?.cost;
  const costLabel = costObj
    ? `${Math.round(costObj.rsd).toLocaleString('sr-RS')} RSD (≈ €${costObj.eur.toFixed(2)})`
    : null;
  const deliveryRate = pct(deliveredVal, total);
  const isSending = campaign.status === 'sending';

  // second ring: Viber shows "seen rate", others show "failure rate"
  const ring2 = isViber
    ? { value: pct(seenVal, deliveredVal), sub: t('Viđeno', 'Seen'), color: 'var(--ch-viber)' }
    : { value: pct(failedVal, total), sub: t('Neuspeh', 'Failure'), color: 'var(--danger)' };

  const remainingMin = Math.max(0, Math.round((total - deliveredVal) / 14 / 60));

  const breakdown = [
    [t('Poslato', 'Sent'), fmt(dispatchedVal)],
    [t('Isporučeno', 'Delivered'), fmt(deliveredVal)],
    isViber ? [t('Viđeno', 'Seen'), fmt(seenVal)] : null,
    [t('Neisporučeno', 'Undelivered'), fmt(undeliveredVal)],
    [t('Neuspelo', 'Failed'), fmt(failedVal)],
    !isViber && stats ? [t('Na čekanju', 'Pending'), fmt(stats.pending)] : null,
    isSuperAdmin && costLabel ? [t('Trošak', 'Cost'), costLabel] : null,
    campaign.startedAt ? [t('Pokrenuto', 'Started'), fmtDate(campaign.startedAt)] : null,
    campaign.completedAt ? [t('Završeno', 'Completed'), fmtDate(campaign.completedAt)] : null,
  ].filter(Boolean) as [string, string][];

  const isDraft = campaign.status === 'draft';

  return (
    <div className="page page-wide">
      <button className="btn btn-ghost btn-sm" style={{ marginBottom: 16, paddingLeft: 6 }} onClick={() => navigate('/campaigns')}>
        <Icon name="chevronLeft" size={16} /> {t('Kampanje', 'Campaigns')}
      </button>

      <div className="page-head">
        <div className="flex items-center gap-16">
          <ChannelBubble channel={ch} />
          <div>
            <div className="flex items-center gap-12 wrap">
              <h1 className="page-title" style={{ fontSize: 24 }}>{campaign.name}</h1>
              <StatusBadge status={campaign.status} />
              {campaign.archived && <span className="chip">{t('Arhivirano', 'Archived')}</span>}
            </div>
            <p className="page-sub" style={{ marginTop: 2 }}>
              {campaign.subject || (isViber ? t('Viber rich poruka', 'Viber rich message') : CHANNEL_META[ch].label)}
            </p>
          </div>
        </div>
        <div className="flex gap-8 wrap">
          <button className="btn btn-secondary btn-sm" onClick={() => setTestOpen(true)}>
            <Icon name="send" size={15} /> {t('Pošalji test', 'Send test')}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={handlePreview}>
            <Icon name="eye" size={15} /> {t('Pregled', 'Preview')}
          </button>
          <button className="btn btn-secondary btn-sm" onClick={handleClone}>
            <Icon name="copy" size={15} /> {t('Dupliraj', 'Duplicate')}
          </button>
          {isDraft && (
            <button className="btn btn-secondary btn-sm" onClick={() => navigate(isViber ? `/campaigns/viber/${id}/edit` : `/campaigns/${id}/edit`)}>
              <Icon name="edit" size={15} /> {t('Izmeni', 'Edit')}
            </button>
          )}
          {isDraft && total > 0 && (
            <button className="btn btn-primary btn-sm" onClick={handleStartCampaign}>
              <Icon name="play" size={15} /> {t('Pokreni slanje', 'Start sending')}
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="card card-pad" style={{ borderColor: 'var(--danger)', color: 'var(--danger-strong)', marginBottom: 'var(--gap)' }}>
          <Icon name="alert" size={16} /> {error}
        </div>
      )}
      {success && (
        <div className="card card-pad" style={{ borderColor: 'var(--ok)', color: 'var(--brand-700)', marginBottom: 'var(--gap)' }}>
          <Icon name="checkCircle" size={16} /> {success}
        </div>
      )}

      {isSending && (
        <div className="card card-pad" style={{ marginBottom: 'var(--gap)', borderColor: 'var(--ch-sms)' }}>
          <div className="flex items-center justify-between wrap gap-16">
            <div className="flex items-center gap-12">
              <span className="badge badge-sending"><span className="dot" />{t('U toku', 'Sending')}</span>
              <span style={{ fontWeight: 700 }}>{fmt(deliveredVal)} / {fmt(total)} {t('poslato', 'sent')}</span>
            </div>
            <span className="muted" style={{ fontSize: 13 }}>{t(`~ 14 poruka/sek · preostalo ≈ ${remainingMin} min`, `~ 14 messages/sec · remaining ≈ ${remainingMin} min`)}</span>
          </div>
          <div className="progress" style={{ marginTop: 14 }}>
            <span style={{ width: pct(deliveredVal + failedVal, total) + '%' }} />
          </div>
        </div>
      )}

      {/* metrics */}
      <div className="stat-grid" style={{ marginBottom: 'var(--gap)' }}>
        <StatTile icon="contacts" label={t('Primaoci', 'Recipients')} value={fmt(total)} />
        <StatTile icon="send" channel="sms" label={t('Poslato', 'Sent')} value={fmt(dispatchedVal)} />
        <StatTile icon="checkCircle" channel="email" label={t('Isporučeno', 'Delivered')} value={fmt(deliveredVal)} />
        <StatTile icon="alert" label={t('Neisporučeno', 'Undelivered')} value={fmt(undeliveredVal)} />
      </div>

      {/* report grid: rates | recipients */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.7fr', gap: 'var(--gap)' }} className="detail-grid">
        <div className="card card-pad">
          <div className="card-title" style={{ marginBottom: 18 }}>{isViber ? t('Isporuka & angažovanje', 'Delivery & engagement') : t('Izveštaj o isporuci', 'Delivery report')}</div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))', gap: 16, justifyItems: 'center', alignItems: 'center' }}>
            <Ring value={deliveryRate} sub={t('Isporuka', 'Delivery')} color="var(--brand-500)" size={130} />
            <Ring value={ring2.value} sub={ring2.sub} color={ring2.color} size={130} />
          </div>
          <div style={{ marginTop: 20, borderTop: '1px solid var(--border)', paddingTop: 8 }}>
            {breakdown.map((r, i) => (
              <div key={i} className="flex items-center justify-between" style={{ padding: '8px 0', fontSize: 14 }}>
                <span className="muted">{r[0]}</span>
                <span className="tnum" style={{ fontWeight: 700 }}>{r[1]}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="card">
          <div className="card-pad flex items-center justify-between wrap gap-12" style={{ paddingBottom: 12 }}>
            <div className="card-title">{t('Primaoci', 'Recipients')}</div>
            <div className="flex items-center gap-8 wrap">
              <select
                className="input input-sm"
                value={recipStatus}
                onChange={(e) => { setRecipStatus(e.target.value); setRecipPage(1); }}
                aria-label={t('Filter po statusu', 'Filter by status')}
              >
                <option value="all">{t('Svi statusi', 'All statuses')}</option>
                <option value="delivered">{t('Isporučeno', 'Delivered')}</option>
                <option value="seen">{t('Viđeno', 'Seen')}</option>
                <option value="undelivered">{t('Neisporučeno', 'Undelivered')}</option>
                <option value="failed">{t('Neuspelo', 'Failed')}</option>
              </select>
              <select
                className="input input-sm"
                value={recipLimit}
                onChange={(e) => { setRecipLimit(parseInt(e.target.value)); setRecipPage(1); }}
                aria-label={t('Po strani', 'Per page')}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span className="dim" style={{ fontSize: 13 }}>{fmt(recipTotal)}</span>
            </div>
          </div>

          <div className="card-pad flex items-center gap-8 wrap" style={{ paddingTop: 0, paddingBottom: 12 }}>
            <span className="dim" style={{ fontSize: 12 }}>{t('Export:', 'Export:')}</span>
            <button className="btn btn-ghost btn-sm" onClick={() => downloadExport('delivered')}>
              <Icon name="download" size={14} /> {t('Isporučeno', 'Delivered')}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => downloadExport('seen')}>
              <Icon name="download" size={14} /> {t('Viđeno', 'Seen')}
            </button>
            <button className="btn btn-ghost btn-sm" onClick={() => downloadExport('undelivered')}>
              <Icon name="download" size={14} /> {t('Neisporučeno', 'Undelivered')}
            </button>
          </div>

          {recipTotal === 0 && recipStatus === 'all' && !recipLoading ? (
            <Empty icon="contacts" title={t('Još nema primaoca', 'No recipients yet')} sub={isDraft ? t('Dodaj liste ili upload-uj Excel ispod.', 'Add lists or upload an Excel file below.') : t('Primaoci će se pojaviti kad se generišu.', 'Recipients will appear once they are generated.')} />
          ) : recipients.length === 0 ? (
            <div className="card-pad muted">{recipLoading ? t('Učitavanje…', 'Loading…') : t('Nema primaoca za ovaj filter.', 'No recipients for this filter.')}</div>
          ) : (
            <>
              <table className="table">
                <thead>
                  <tr>
                    <th style={{ paddingLeft: 22 }}>{t('Ime', 'Name')}</th>
                    <th>{isViber || isSms ? t('Telefon', 'Phone') : 'Email'}</th>
                    <th>{t('Status', 'Status')}</th>
                  </tr>
                </thead>
                <tbody>
                  {recipients.map((r, i) => {
                    const log = Array.isArray(r.sendLogs) ? r.sendLogs[0] : undefined;
                    const st: string | undefined = log?.status;
                    const failed = st === 'failed' || st === 'bounced';
                    const delivered = st === 'delivered' || st === 'seen';
                    const dispatched = st === 'sent' || st === 'queued';
                    return (
                      <tr key={r.id ?? i}>
                        <td style={{ paddingLeft: 22 }}>
                          <div className="flex items-center gap-12">
                            <span className="av-mini" style={{ background: 'var(--brand-600)' }}>{recipientInitials(r.name, r.email)}</span>
                            <span style={{ fontWeight: 600, fontSize: 13.5 }}>{r.name || '—'}</span>
                          </div>
                        </td>
                        <td className="muted mono" style={{ fontSize: 13 }}>{isViber || isSms ? (r.phoneNumber || '—') : (r.email || '—')}</td>
                        <td>
                          {failed ? (
                            <span className="badge badge-failed"><span className="dot" />{t('Neuspelo', 'Failed')}</span>
                          ) : delivered ? (
                            <span className="ch-tag email" style={{ fontSize: 11.5 }}><Icon name="check" size={12} /> {t('Isporučeno', 'Delivered')}</span>
                          ) : dispatched ? (
                            <span className="badge badge-sending"><span className="dot" />{t('Poslato', 'Sent')}</span>
                          ) : (
                            <span className="badge badge-draft"><span className="dot" />{t('Na čekanju', 'Pending')}</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {recipTotalPages > 1 && (
                <div className="card-pad" style={{ paddingTop: 12 }}>
                  <Pagination currentPage={recipPage} totalPages={recipTotalPages} onPageChange={setRecipPage} />
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* draft-only: recipients upload + scheduling */}
      {isDraft && (
        <div style={{ display: 'grid', gridTemplateColumns: '1.7fr 1fr', gap: 'var(--gap)', marginTop: 'var(--gap)' }} className="detail-grid">
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
              <div className="card-title">{t('Dodaj primaoce (Excel)', 'Add recipients (Excel)')}</div>
              <button className="btn btn-ghost btn-sm" onClick={handleDownloadTemplate}>
                <Icon name="download" size={15} /> {t('Šablon', 'Template')}
              </button>
            </div>
            <div
              onClick={() => document.getElementById('file-input')?.click()}
              style={{ border: '1.5px dashed var(--border-strong)', borderRadius: 'var(--r-md)', padding: '24px 16px', textAlign: 'center', cursor: 'pointer', color: 'var(--text-2)' }}
            >
              <input id="file-input" type="file" accept=".xlsx,.xls" onChange={handleFileChange} style={{ display: 'none' }} />
              {uploadFile ? (
                <span><Icon name="checkCircle" size={16} /> {uploadFile.name}</span>
              ) : (
                <span><Icon name="paperclip" size={16} /> {t('Klikni ili prevuci Excel fajl', 'Click or drag an Excel file')}</span>
              )}
            </div>
            {uploadFile && (
              <div className="flex gap-8 mt-16">
                <button className="btn btn-primary btn-sm" onClick={handleUpload} disabled={uploading}>
                  {uploading ? t('Upload u toku…', 'Uploading…') : <><Icon name="upload" size={15} /> {t('Upload primaoca', 'Upload recipients')}</>}
                </button>
                <button className="btn btn-ghost btn-sm" onClick={() => setUploadFile(null)}>{t('Otkaži', 'Cancel')}</button>
              </div>
            )}
            <div className="hint" style={{ marginTop: 14 }}>
              {t('Excel mora imati kolonu', 'The Excel file must have a')} <strong>„email"</strong> {t('(obavezno). Kolona', 'column (required). The')} <strong>„name"</strong> {t('je opciona; ostale postaju custom polja', 'column is optional; the rest become custom fields')} ({'{{ime_kolone}}'}).
            </div>
          </div>

          <div className="card card-pad">
            <div className="card-title" style={{ marginBottom: 12 }}>{t('Zakazivanje', 'Scheduling')}</div>
            <ScheduleBox campaign={campaign} onChanged={loadCampaign} />
          </div>
        </div>
      )}

      {/* Test send modal */}
      {testOpen && (
        <div className="overlay" onClick={() => setTestOpen(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 className="card-title">{t('Pošalji test', 'Send test')} — {isViber ? 'Viber' : isSms ? 'SMS' : 'Email'}</h3>
              <button className="icon-btn" aria-label={t('Zatvori', 'Close')} onClick={() => setTestOpen(false)}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="modal-body">
              <TestSendBox campaign={campaign} />
            </div>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {showPreview && (
        <div className="overlay" onClick={() => setShowPreview(false)}>
          <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
            <div className="modal-head">
              <h3 className="card-title">{viberPreview ? `${t('Pregled', 'Preview')} — Viber` : `${t('Pregled', 'Preview')} — Email`}</h3>
              <button className="icon-btn" aria-label={t('Zatvori', 'Close')} onClick={() => setShowPreview(false)}>
                <Icon name="x" size={18} />
              </button>
            </div>
            <div className="modal-body">
              {viberPreview ? (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  <ViberMessagePreview
                    viberContent={viberPreview}
                    recipientData={{ name: 'Marko Marković', phoneNumber: '+381641234567', customData: {} }}
                  />
                </div>
              ) : (
                <div dangerouslySetInnerHTML={{ __html: previewHtml }} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CampaignDetail;
